import type { JSX } from 'react';
import { useI18n } from '../lib/i18n.js';
import { Card, Filter, Row, Toggle } from './ui.js';

export type AssigneeMode = 'ANYONE' | 'ME' | 'UNASSIGNED' | 'GROUP';

export interface RuleDraft {
  id?: string;
  name: string;
  enabled: boolean;
  events: string[];
  projects: string[];
  issueTypes: string[];
  statuses: string[];
  assigneeMode: AssigneeMode;
  assigneeGroups: string[];
  minPriorityName: string | null;
  staleHours: number | null;
}

export interface RuleOptions {
  projects: Array<{ key: string; name: string }>;
  issueTypes: string[];
  statuses: string[];
  priorities: string[];
  groups: string[];
  events: Array<{ event: string; category: string }>;
}

export function emptyRule(name: string): RuleDraft {
  return {
    name,
    enabled: true,
    events: [],
    projects: [],
    issueTypes: [],
    statuses: [],
    assigneeMode: 'ANYONE',
    assigneeGroups: [],
    minPriorityName: null,
    staleHours: null,
  };
}

/**
 * A multi-select where "nothing selected" means "everything".
 *
 * That default is what keeps a rule readable: a person picks only the things
 * they want to narrow, and an untouched row says "any" rather than forcing
 * them to tick every option to mean the same thing.
 */
function ChipPicker({
  label,
  anyLabel,
  options,
  selected,
  onChange,
}: {
  label: string;
  anyLabel: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
}): JSX.Element {
  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((entry) => entry !== value)
        : [...selected, value],
    );
  };

  return (
    <div className="field">
      <label>{label}</label>
      <div className="filter-wrap">
        <Filter label={anyLabel} active={selected.length === 0} onClick={() => onChange([])} />
        {options.map((option) => (
          <Filter
            key={option.value}
            label={option.label}
            active={selected.includes(option.value)}
            onClick={() => toggle(option.value)}
          />
        ))}
      </div>
    </div>
  );
}

export function RuleEditor({
  draft,
  options,
  onChange,
  onSave,
  onDelete,
  onCancel,
  saving,
}: {
  draft: RuleDraft;
  options: RuleOptions;
  onChange: (next: RuleDraft) => void;
  onSave: () => void;
  onDelete?: () => void;
  onCancel: () => void;
  saving: boolean;
}): JSX.Element {
  const { t } = useI18n();
  const set = <K extends keyof RuleDraft>(key: K, value: RuleDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const assigneeModes: Array<[AssigneeMode, string]> = [
    ['ANYONE', t('notify.rule_anyone')],
    ['ME', t('notify.rule_me')],
    ['GROUP', t('notify.rule_group')],
    ['UNASSIGNED', t('notify.rule_unassigned')],
  ];

  const staleChoices: Array<[number | null, string]> = [
    [null, t('notify.rule_stale_off')],
    [4, t('notify.rule_hours', { hours: 4 })],
    [24, t('notify.rule_hours', { hours: 24 })],
    [72, t('notify.rule_days', { days: 3 })],
    [168, t('notify.rule_days', { days: 7 })],
  ];

  return (
    <Card title={draft.id ? draft.name : t('notify.rule_new')}>
      <div className="field">
        <label htmlFor="rule-name">{t('notify.rule_name')}</label>
        <input
          id="rule-name"
          value={draft.name}
          onChange={(event) => set('name', event.target.value)}
        />
      </div>

      <Toggle
        label={t('notify.master')}
        checked={draft.enabled}
        onChange={(next) => set('enabled', next)}
      />

      <ChipPicker
        label={t('notify.rule_events')}
        anyLabel={t('notify.rule_any')}
        options={options.events.map((entry) => ({ value: entry.event, label: entry.event }))}
        selected={draft.events}
        onChange={(next) => set('events', next)}
      />

      <ChipPicker
        label={t('notify.rule_projects')}
        anyLabel={t('notify.rule_all_projects')}
        options={options.projects.map((project) => ({
          value: project.key,
          label: `${project.key} · ${project.name}`,
        }))}
        selected={draft.projects}
        onChange={(next) => set('projects', next)}
      />

      <ChipPicker
        label={t('notify.rule_issue_types')}
        anyLabel={t('notify.rule_any')}
        options={options.issueTypes.map((name) => ({ value: name, label: name }))}
        selected={draft.issueTypes}
        onChange={(next) => set('issueTypes', next)}
      />

      <ChipPicker
        label={t('notify.rule_statuses')}
        anyLabel={t('notify.rule_any')}
        options={options.statuses.map((name) => ({ value: name, label: name }))}
        selected={draft.statuses}
        onChange={(next) => set('statuses', next)}
      />

      <div className="field">
        <label>{t('notify.rule_assignee')}</label>
        <div className="filter-wrap">
          {assigneeModes.map(([mode, label]) => (
            <Filter
              key={mode}
              label={label}
              active={draft.assigneeMode === mode}
              onClick={() => set('assigneeMode', mode)}
            />
          ))}
        </div>
      </div>

      {draft.assigneeMode === 'GROUP' ? (
        <ChipPicker
          label={t('notify.rule_pick_groups')}
          anyLabel={t('notify.rule_any')}
          options={options.groups.map((name) => ({ value: name, label: name }))}
          selected={draft.assigneeGroups}
          onChange={(next) => set('assigneeGroups', next)}
        />
      ) : null}

      {options.priorities.length > 0 ? (
        <Row
          title={t('issue.priority')}
          right={
            <select
              value={draft.minPriorityName ?? ''}
              onChange={(event) => set('minPriorityName', event.target.value || null)}
              style={{ width: 'auto' }}
            >
              <option value="">{t('notify.rule_any')}</option>
              {options.priorities.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          }
        />
      ) : null}

      <div className="field">
        <label>{t('notify.rule_stale')}</label>
        <div className="filter-wrap">
          {staleChoices.map(([hours, label]) => (
            <Filter
              key={String(hours)}
              label={label}
              active={draft.staleHours === hours}
              onClick={() => set('staleHours', hours)}
            />
          ))}
        </div>
      </div>

      <div className="btn-row">
        <button
          type="button"
          className="btn"
          disabled={saving || draft.name.trim().length === 0}
          onClick={onSave}
        >
          {t('common.save')}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        {onDelete ? (
          <button type="button" className="btn btn-danger" onClick={onDelete}>
            {t('notify.rule_delete')}
          </button>
        ) : null}
      </div>
    </Card>
  );
}
